import React, { useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import MurphyLogo from "@/components/shared/MurphyLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, BarChart3, Users, LogIn } from "lucide-react";

function GoogleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.66 4.1-5.5 4.1-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.9 3.1 14.7 2 12 2 6.9 2 2.8 6.1 2.8 11.2S6.9 20.4 12 20.4c5.9 0 9.8-4.1 9.8-9.9 0-.7-.1-1.2-.2-1.7H12z" />
    </svg>
  );
}

export default function Login() {
  const { login, loginWithPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await loginWithPassword(email.trim().toLowerCase(), password);
      // AuthContext flips isAuthenticated -> AppRouter renders the app
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Innlogging feilet. Sjekk e-post og passord.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-sm animate-fade-in">
          <MurphyLogo size="lg" />
          <h1 className="mt-10 text-3xl font-extrabold text-brand-ink tracking-tight">Velkommen tilbake</h1>
          <p className="mt-2 text-slate-500">Logg inn for å administrere kunder, oppkjøp og investeringer.</p>

          <form onSubmit={handlePasswordLogin} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="login-email">E-post</Label>
              <Input
                id="login-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="deg@murphy.no"
                className="mt-1.5 h-11"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="login-password">Passord</Label>
              <Input
                id="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 h-11"
                data-testid="login-password-input"
              />
            </div>
            {error && <p className="text-sm text-rose-600" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={submitting || !email || !password} className="w-full h-11 gap-2" data-testid="login-submit-button">
              <LogIn className="w-4 h-4" /> {submitting ? "Logger inn..." : "Logg inn"}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
            <span className="h-px flex-1 bg-slate-200" /> eller <span className="h-px flex-1 bg-slate-200" />
          </div>

          <Button onClick={login} className="w-full h-12 gap-3 bg-white text-brand-ink border border-slate-200 hover:bg-slate-50 shadow-sm text-[15px]" data-testid="login-google-button">
            <GoogleIcon /> Logg inn med Google
          </Button>

          <p className="mt-6 text-xs text-slate-400 leading-relaxed">
            Ved å logge inn godtar du Murphy sine vilkår for bruk. Nye kontoer må godkjennes av en administrator.
          </p>
        </div>
      </div>

      {/* Right: brand panel */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-brand-ink text-white items-center justify-center p-12">
        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-brand-lime/20 blur-3xl" />
        <div className="absolute -left-16 -bottom-24 w-80 h-80 rounded-full bg-brand-lime/10 blur-3xl" />
        <div className="relative max-w-md">
          <h2 className="text-4xl font-extrabold leading-tight tracking-tight">
            Alt som kan gå galt<span className="text-brand-lime">.</span>
            <br />Vi tar oss av regningen.
          </h2>
          <p className="mt-4 text-white/60">Én plattform for kunder, oppkjøpskandidater, investorer, prosjekter og økonomi.</p>
          <div className="mt-10 space-y-4">
            {[
              { icon: Users, text: "Kunde- og investoroppfølging på ett sted" },
              { icon: BarChart3, text: "Moderne rapporter og investeringsanalyse" },
              { icon: ShieldCheck, text: "Innlogging med Google eller e-post og passord" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <f.icon className="w-5 h-5 text-brand-lime" />
                </div>
                <span className="text-white/80">{f.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
